import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BOOKING_FOCUSED_PARAM, BOOKING_HUB_URL } from '../utils/bookingLinks';
import './VoucherPurchasePage.css';

const VoucherPurchasePage = () => {
  const { t } = useTranslation();
  const { isAuthenticated, instructor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusedEntry = searchParams.get(BOOKING_FOCUSED_PARAM) === '1';
  const [pricing, setPricing] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    amount: '',
    payment_method: 'bank_transfer',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await axios.get('/api/pricing/active');
        setPricing(response.data.pricing || []);
        if (response.data.pricing?.length > 0 && !formData.amount) {
          setFormData(prev => ({ ...prev, amount: String(response.data.pricing[0].amount) }));
        }
      } catch (err) {
        console.error('Árak betöltési hiba:', err);
      }
    };
    loadPricing();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.customer_name?.trim()) {
      setError(t('voucher.errName'));
      return;
    }
    if (!formData.customer_email?.trim()) {
      setError(t('voucher.errEmail'));
      return;
    }
    if (!formData.customer_phone?.trim()) {
      setError(t('voucher.errPhone'));
      return;
    }
    const amount = Number(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError(t('voucher.errAmount'));
      return;
    }

    const paymentMethod = formData.payment_method || 'bank_transfer';
    const revolutLink = paymentMethod === 'revolut'
      ? (pricing.find(p => Number(p.amount) === amount)?.revolut_link || pricing[0]?.revolut_link)
      : null;

    setSubmitting(true);
    try {
      await axios.post('/api/voucher/purchase', {
        customer_name: formData.customer_name.trim(),
        customer_email: formData.customer_email.trim(),
        customer_phone: formData.customer_phone.trim(),
        amount,
        payment_method: paymentMethod,
        revolut_link: revolutLink,
        notes: formData.notes?.trim() || null
      });
      const successMessage = paymentMethod === 'revolut' && revolutLink
        ? t('voucher.successRevolut')
        : t('voucher.successBank');
      setFormData({ customer_name: '', customer_email: '', customer_phone: '', amount: pricing[0] ? String(pricing[0].amount) : '', payment_method: 'bank_transfer', notes: '' });
      navigate('/booking/thank-you?type=voucher', { state: { message: successMessage, revolut_link: revolutLink } });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('voucher.errGeneric');
      const details = err.response?.data?.details;
      setError(details ? `${msg}\n${details}` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="voucher-purchase-page container">
      <h1>{t('voucher.title')}</h1>
      <p className="voucher-intro">
        {t('voucher.intro')}
      </p>

      {error && <div className="error" role="alert">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card voucher-form-card">
        <h2>{t('voucher.enterDetails')}</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label>{t('voucher.name')} {t('common.required')}</label>
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('voucher.email')} {t('common.required')}</label>
            <input
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('voucher.phone')} {t('common.required')}</label>
            <input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              required
            />
          </div>
          {pricing.length > 0 ? (
            <div className="form-group">
              <label>{t('voucher.selectAmount')} {t('common.required')}</label>
              <div className="voucher-pricing-options">
                {pricing.map((p) => (
                  <label key={p.id} className="voucher-pricing-option">
                    <input
                      type="radio"
                      name="amount"
                      value={p.amount}
                      checked={Number(formData.amount) === Number(p.amount)}
                      onChange={() => setFormData({ ...formData, amount: String(p.amount) })}
                    />
                    <span>
                      {p.name} – {parseInt(p.amount).toLocaleString('en-GB')} €
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>{t('voucher.amount')} {t('common.required')}</label>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="e.g. 50"
              />
            </div>
          )}
          <div className="form-group">
            <label>{t('voucher.paymentMethod')} {t('common.required')}</label>
            <div className="voucher-pricing-options">
              <label className="voucher-pricing-option">
                <input
                  type="radio"
                  name="payment_method"
                  value="bank_transfer"
                  checked={formData.payment_method === 'bank_transfer'}
                  onChange={() => setFormData({ ...formData, payment_method: 'bank_transfer' })}
                />
                <span>{t('voucher.bankTransfer')}</span>
              </label>
              <label className="voucher-pricing-option">
                <input
                  type="radio"
                  name="payment_method"
                  value="revolut"
                  checked={formData.payment_method === 'revolut'}
                  onChange={() => setFormData({ ...formData, payment_method: 'revolut' })}
                />
                <span>{t('voucher.revolut')}</span>
              </label>
            </div>
            {formData.payment_method === 'revolut' && pricing.length > 0 && !pricing.find(p => Number(p.amount) === Number(formData.amount || 0))?.revolut_link && (
              <small className="form-hint">{t('voucher.revolutHint')}</small>
            )}
          </div>
          <div className="form-group">
            <label>{t('voucher.notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? t('voucher.submitting') : t('voucher.requestPayment')}
            </button>
            {!focusedEntry && (
              <>
                <Link to={BOOKING_HUB_URL} className="btn btn-secondary">{t('voucher.bookSlot')}</Link>
                <Link to="/booking/companions" className="btn btn-secondary">{t('voucher.companionsHint')}</Link>
              </>
            )}
          </div>
          {focusedEntry && (
            <p className="voucher-focused-footer">
              <Link to={BOOKING_HUB_URL}>{t('booking.openFullBookingMenu')}</Link>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default VoucherPurchasePage;
